import { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { evOnShowRewardsTransparencyClick } from 'events/global';
import VipOnlyLogo from 'components/icons/vipDashboard/VipOnlyLogo';
import MartyLink from 'components/common/MartyLink';
import ProductUtils from 'helpers/ProductUtils';
import { toUSD } from 'helpers/NumberFormats';
import { track } from 'apis/amethyst';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';

import css from 'styles/components/common/rewardsTransparency.scss';

export class RewardsTransparency extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  };

  handleCtaClick = () => {
    const { isForCartModal, pageType, isRecognizedCustomer, rewardsForCurrentPage, rewards } = this.props;
    track(() => ([
      evOnShowRewardsTransparencyClick, { isForCartModal, pageType, rewards, isRecognizedCustomer, rewardsForCurrentPage }
    ]));
  };

  makeRecognizedStatusText = () => {
    const { handleCtaClick } = this;
    const { isRecognizedCustomer, rewards: { isEnrolled } } = this.props;
    const { testId } = this.context;

    if (isRecognizedCustomer && isEnrolled) {
      return '';
    }

    if (isRecognizedCustomer && !isEnrolled) {
      return <>Not a Zappos VIP member? <Link onClick={handleCtaClick} to="/vip/dashboard">Join today to start earning!</Link></>;
    }

    return <Link onClick={handleCtaClick} to="/vip/dashboard" data-test-id={testId('vipDashboardLink')}>Sign in to get started!</Link>;
  };

  makeText = () => {
    const { makeRecognizedStatusText } = this;
    const {
      isForCartModal, pageType, pointsForCurrentPage,
      rewards: { transparencyPointsForCart }
    } = this.props;
    const { testId } = this.context;

    const cartPoints = transparencyPointsForCart?.totalPoints?.dollarValue;
    if (isForCartModal) {
      return !!cartPoints && (
        <p>Check out to earn Zappos VIP points worth up to <span data-test-id={testId('pointsForModal')}>{toUSD(cartPoints, false)}</span> in VIP codes.</p>
      );
    }

    if (pageType === 'cart' && pointsForCurrentPage) {
      return (
        <p>Check out to earn Zappos VIP points worth up to <span data-test-id={testId('pointsForCurrentPage')}>{pointsForCurrentPage}</span> in VIP codes. {makeRecognizedStatusText()}</p>
      );
    }

    if (pageType === 'product' && pointsForCurrentPage) {
      return (
        <p>Earn Zappos VIP points worth up to <span>{pointsForCurrentPage}</span> in VIP codes on this item. {makeRecognizedStatusText()}</p>
      );
    }

    return null;

  };

  render() {
    const { makeText } = this;
    const {
      isForCartModal,
      pageType,
      isGiftCard,
      accountRewards = {}
    } = this.props;
    const { testId, marketplace: { hasRewardsTransparency } } = this.context;
    if (!hasRewardsTransparency || (pageType === 'product' && isGiftCard && !isForCartModal)) {
      return null;
    }

    if (pageType === 'account') {
      const { rewardsInfo } = accountRewards;
      return rewardsInfo && !rewardsInfo.consented && rewardsInfo.canEnroll && <div
        className={cn(css.page, { [css.account]: pageType === 'account' && !isForCartModal })}
        data-test-id={testId('rewardsTransparencyContainer')}>
        <VipOnlyLogo />
        <p><MartyLink data-test-id={testId('viewDashboard')} to="/vip/dashboard">Become a Zappos VIP for <span>FREE</span>!</MartyLink> VIP members get Free Next Business Day Shipping on every order and more!</p>
      </div>;
    }

    const rewardsText = makeText();

    return !!rewardsText && (
      <div
        className={cn(css.page, {
          [css.cart]: pageType === 'cart' && !isForCartModal,
          [css.pdp]: pageType === 'product' && !isForCartModal,
          [css.brand]: pageType === 'brand' && !isForCartModal,
          [css.checkout]: pageType === 'checkout' && !isForCartModal,
          [css.cartModal]: !!isForCartModal
        })}
        data-test-id={testId('rewardsTransparencyContainer')}>
        {rewardsText}
      </div>
    );
  }
}

const pointsToPageMap = {
  'cart': 'transparencyPointsForCart',
  'checkout': 'transparencyPointsForCart',
  'homepage': 'transparencyPointsForBrand',
  'landing': 'transparencyPointsForBrand',
  'product': 'transparencyPointsForItem'
};

const mapStateToProps = state => {
  const { product, rewards: accountRewards, sharedRewards: rewards, cookies: { 'x-main': xMain }, pageView: { pageType } } = state;
  const isRecognizedCustomer = !!xMain;
  const rewardsForCurrentPage = rewards[pointsToPageMap[pageType]];
  const pointsForCurrentPage = toUSD(rewardsForCurrentPage?.totalPoints?.dollarValue, false);

  return {
    accountRewards,
    rewards,
    pageType,
    rewardsForCurrentPage,
    pointsForCurrentPage,
    isRecognizedCustomer,
    isGiftCard: ProductUtils.isGiftCard(product?.detail?.defaultProductType)
  };
};

RewardsTransparency.displayName = 'RewardsTransparency';

export default withErrorBoundary('RewardsTransparency', connect(mapStateToProps)(RewardsTransparency));
